/**
 * F5. Экзамен на права: раз в жизнь в 18–40 лет при деньгах ≥ 0,5 ВВП на
 * душу. Три ситуации на перекрёстке из банка, в каждой — «ехать» или
 * «ждать»; ≥ 2 верных — права (`flags.license`). Экзамен стоит 5 % ВВП
 * на душу (не больше денег) независимо от исхода. Движок читает флаг:
 * риск `health.accident` × 1,3 (честно показано в `accidentMult`) и
 * +1 уровень при следующем найме. «Пропустить» — экзамен не сдавали:
 * ни прав, ни расходов; второго предложения нет (`flags.drivingDone`).
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { LICENSE_ACCIDENT_MULT, LICENSE_JOB_LEVEL_BONUS, FLAG_LICENSE, type Life, type MinigameOutcome, type Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { gdpPpp } from '../casino/common';
import { nothing, shuffle } from './common';

export const DRIVING_MIN_AGE = 18;
export const DRIVING_MAX_AGE = 40;
/** Деньги не меньше такой доли ВВП на душу. */
export const DRIVING_MIN_MONEY_GDP = 0.5;
/** Цена экзамена — доля ВВП на душу. */
export const DRIVING_COST_GDP = 0.05;
export const DRIVING_SITUATIONS = 3;
export const DRIVING_PASS_CORRECT = 2;
export const FLAG_DRIVING_DONE = 'drivingDone';

export type DrivingChoice = 'go' | 'wait';

export interface DrivingSituation {
  key: string;
  correct: DrivingChoice;
}

export const DRIVING_BANK: readonly DrivingSituation[] = [
  { key: 'minigame.driving.s.0', correct: 'wait' },
  { key: 'minigame.driving.s.1', correct: 'go' },
  { key: 'minigame.driving.s.2', correct: 'wait' },
  { key: 'minigame.driving.s.3', correct: 'go' },
  { key: 'minigame.driving.s.4', correct: 'wait' },
  { key: 'minigame.driving.s.5', correct: 'go' },
  { key: 'minigame.driving.s.6', correct: 'wait' },
  { key: 'minigame.driving.s.7', correct: 'go' },
];

export interface DrivingState {
  situations: DrivingSituation[];
  cost: number;
  /** Множитель риска аварии с правами — показывается на экране. */
  accidentMult: number;
  levelBonus: number;
}

export type DrivingInput = { choices: DrivingChoice[] };

export const driving: MinigameDef<DrivingInput, DrivingState> = {
  id: 'driving',
  section: 'F',
  triggers: [{ kind: 'age', min: DRIVING_MIN_AGE, max: DRIVING_MAX_AGE, everyYears: 1 }],
  available(life: Life, ds: Dataset, _law: GamblingLaw): boolean {
    if (life.age < DRIVING_MIN_AGE || life.age > DRIVING_MAX_AGE) return false;
    if (life.flags[FLAG_DRIVING_DONE] === true || life.flags[FLAG_LICENSE] === true) return false;
    try {
      return life.money >= DRIVING_MIN_MONEY_GDP * gdpPpp(ds, life);
    } catch {
      return false;
    }
  },
  setup(life: Life, ds: Dataset, rng: Rng): DrivingState {
    const cost = Math.min(Math.max(0, life.money), DRIVING_COST_GDP * gdpPpp(ds, life));
    return {
      situations: shuffle(DRIVING_BANK, rng).slice(0, DRIVING_SITUATIONS),
      cost,
      accidentMult: LICENSE_ACCIDENT_MULT,
      levelBonus: LICENSE_JOB_LEVEL_BONUS,
    };
  },
  play(state: DrivingState, input: DrivingInput, _life: Life): MinigameOutcome {
    const choices = Array.isArray(input.choices) ? input.choices : [];
    let right = 0;
    state.situations.forEach((s, i) => {
      if (choices[i] === s.correct) right += 1;
    });
    const passed = right >= DRIVING_PASS_CORRECT;
    return {
      ...nothing(),
      moneyDelta: -state.cost,
      flags: passed ? { [FLAG_DRIVING_DONE]: true, [FLAG_LICENSE]: true } : { [FLAG_DRIVING_DONE]: true },
      logKey: passed ? 'minigame.driving.pass' : 'minigame.driving.fail',
      logParams: { correct: right, total: state.situations.length, amount: Math.round(state.cost) },
    };
  },
  expected(): MinigameOutcome {
    return { ...nothing(), flags: { [FLAG_DRIVING_DONE]: true } };
  },
};
